/**
 * Invitation email-confirmation callback helpers.
 * Never logs tokens. Never accepts an absolute redirect supplied by the user.
 */

export const AUTH_CALLBACK_PATH = "/auth/callback" as const;
export const TRADIVO_PRODUCTION_ORIGIN = "https://tradivo.com.au" as const;

const INVITE_RE = /^[0-9a-fA-F]{64}$/;

export type EmailOtpType =
  | "signup"
  | "invite"
  | "magiclink"
  | "recovery"
  | "email_change"
  | "email";

export type AuthCallbackParams = {
  tokenHash: string | null;
  otpType: EmailOtpType | null;
  code: string | null;
  inviteToken: string | null;
};

export type InvitationAccountStatus = "none" | "unconfirmed" | "confirmed";

export function invitationTokenLooksWellFormedHex(token: string | null | undefined): boolean {
  return INVITE_RE.test((token ?? "").trim());
}

export function buildConfirmationRedirectTo(inviteToken: string, origin: string = TRADIVO_PRODUCTION_ORIGIN): string | null {
  const token = inviteToken.trim();
  if (!invitationTokenLooksWellFormedHex(token)) return null;
  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (parsed.hostname.toLowerCase() !== "tradivo.com.au") return null;
  const url = new URL(AUTH_CALLBACK_PATH, `${parsed.protocol}//${parsed.host}`);
  url.searchParams.set("invite", token);
  return url.toString();
}

export function isSafeInternalAuthPath(path: string): boolean {
  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return false;
  if (trimmed.includes("://") || trimmed.includes("\\")) return false;
  const pathname = trimmed.split("?")[0]?.split("#")[0] ?? "";
  return (
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === AUTH_CALLBACK_PATH ||
    pathname === "/dashboard"
  );
}

export function invitationPathAfterConfirmation(inviteToken: string | null): string {
  const token = (inviteToken ?? "").trim();
  if (!invitationTokenLooksWellFormedHex(token)) return "/login";
  return `/login?invite=${encodeURIComponent(token)}`;
}

export function parseAuthCallbackSearch(search: string, hash = ""): AuthCallbackParams {
  const query = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const hashQuery = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const tokenHash = query.get("token_hash") || hashQuery.get("token_hash");
  const typeRaw = (query.get("type") || hashQuery.get("type") || "").trim().toLowerCase();
  const otpType = isEmailOtpType(typeRaw) ? typeRaw : tokenHash ? "signup" : null;
  const code = query.get("code") || hashQuery.get("code");
  const invite = query.get("invite") || hashQuery.get("invite");
  return {
    tokenHash: tokenHash?.trim() || null,
    otpType,
    code: code?.trim() || null,
    inviteToken: invitationTokenLooksWellFormedHex(invite) ? invite!.trim() : null,
  };
}

export function isEmailOtpType(value: string): value is EmailOtpType {
  return value === "signup" || value === "invite" || value === "magiclink" || value === "recovery" || value === "email_change" || value === "email";
}

export function urlHasAuthCallbackCredentials(search: string, hash = ""): boolean {
  const params = parseAuthCallbackSearch(search, hash);
  return Boolean(params.tokenHash || params.code || /access_token=/i.test(hash));
}

export function redactAuthCallbackText(value: string): string {
  return value
    .replace(/token_hash=[^&\s#]+/gi, "token_hash=[redacted]")
    .replace(/[?&]code=[^&\s#]+/gi, (m) => `${m[0]}code=[redacted]`)
    .replace(/access_token=[^&\s#]+/gi, "access_token=[redacted]")
    .replace(/refresh_token=[^&\s#]+/gi, "refresh_token=[redacted]");
}

export function invitationAcceptanceMode(lookup: {
  ok?: boolean;
  account_exists?: boolean;
  account_status?: InvitationAccountStatus | string;
  email_confirmed?: boolean;
} | null | undefined): "register" | "sign_in" | "confirm_email" | "closed" {
  if (!lookup?.ok) return "closed";
  const status = lookup.account_status;
  if (status === "unconfirmed") return "confirm_email";
  if (status === "confirmed") return "sign_in";
  if (status === "none") return "register";
  if (lookup.account_exists === true && lookup.email_confirmed === false) return "confirm_email";
  if (lookup.account_exists === true && lookup.email_confirmed === true) return "sign_in";
  if (lookup.account_exists) return "sign_in";
  return "register";
}

export type AuthCredentialConsumer = {
  verifyOtp: (opts: { token_hash: string; type: EmailOtpType }) => Promise<{ error: { message: string } | null }>;
  exchangeCodeForSession: (code: string) => Promise<{ error: { message: string } | null }>;
  getSession: () => Promise<{ data: { session: unknown | null } }>;
};

export async function consumeAuthCallbackCredential(
  client: AuthCredentialConsumer,
  params: AuthCallbackParams,
): Promise<{ ok: true; method: "verifyOtp" | "exchangeCodeForSession" | "existing_session" } | { ok: false; error: string }> {
  if (params.tokenHash) {
    const type = params.otpType ?? "signup";
    const { error } = await client.verifyOtp({ token_hash: params.tokenHash, type });
    if (error) return { ok: false, error: error.message };
    return { ok: true, method: "verifyOtp" };
  }
  if (params.code) {
    const existing = await client.getSession();
    if (existing.data.session) return { ok: true, method: "existing_session" };
    const { error } = await client.exchangeCodeForSession(params.code);
    if (error) {
      const again = await client.getSession();
      if (again.data.session) return { ok: true, method: "existing_session" };
      return { ok: false, error: error.message };
    }
    return { ok: true, method: "exchangeCodeForSession" };
  }
  const existing = await client.getSession();
  if (existing.data.session) return { ok: true, method: "existing_session" };
  return { ok: false, error: "This confirmation link is invalid or has expired." };
}
